import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Clock, ChevronDown } from 'lucide-react';
import usePendingOrdersStore from '@/store/pendingOrders';
import { onOrderStatusChanged, getSocket } from '@/services/socket';
import { listTraderOrders } from '@/services/orders';
import { getAppInfoSettings } from '@/services/settings';
import { useAuth } from '@/context/AuthContext';
import { useRate } from '@/context/RateContext';
import { toast } from 'react-toastify';
import { playDingSound, playRejectionSound } from '@/utils/sound';

/**
 * Format number to Persian with commas
 */
function formatPersianNumber(num) {
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
  const englishDigits = '0123456789';
  
  const numStr = num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return numStr.replace(/[0-9]/g, (char) => persianDigits[englishDigits.indexOf(char)]);
}

/**
 * Format seconds to MM:SS or SS
 */
function formatCountdown(seconds) {
  if (seconds <= 0) return '۰۰:۰۰';
  
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  
  const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
  const formatNum = (n) => {
    const str = n.toString().padStart(2, '0');
    return str.replace(/[0-9]/g, (char) => persianDigits[char]);
  };
  
  return `${formatNum(mins)}:${formatNum(secs)}`;
}

function getOrdersArray(response) {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.orders)) return response.orders;
  return [];
}

function fetchTraderOrdersForPendingNotifications() {
  return listTraderOrders({
    PageNumber: 1,
    PageSize: 10,
  });
}

export default function PendingOrdersNotification() {
  const { user, isAuthenticated } = useAuth();
  const { bumpRejectCooldown } = useRate();
  const {
    orders,
    isMinimized,
    setRejectionSeconds,
    setMinimized,
    addPendingOrder,
    removePendingOrder,
    updateCountdown,
    initializePendingOrders,
  } = usePendingOrdersStore();
  const [isInitialized, setIsInitialized] = useState(false);

  // Read info + order history together on refresh, then rebuild pending timers.
  useEffect(() => {
    if (!isAuthenticated || isInitialized) return;

    let isCancelled = false;

    const init = async () => {
      try {
        console.log('[PendingOrdersNotification] initializing pending orders from info + traderorderlist');
        const [settings, ordersResponse] = await Promise.all([
          getAppInfoSettings(),
          fetchTraderOrdersForPendingNotifications(),
        ]);

        if (isCancelled) return;

        const seconds = parseInt(settings?.pending_order_rejection_seconds || '120', 10) || 120;
        setRejectionSeconds(seconds);
        await initializePendingOrders(() => ordersResponse, seconds);

        if (isCancelled) return;
        setIsInitialized(true);
      } catch (error) {
        if (isCancelled) return;
        console.error('Error initializing pending orders:', error);
        setRejectionSeconds(120);
      }
    };

    init();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, isInitialized, initializePendingOrders, setRejectionSeconds]);

  // Join user room for WebSocket events
  useEffect(() => {
    if (!user?.id) return;

    const socket = getSocket();
    socket.emit('join', `user:${user.id}`);

    return () => {
      socket.emit('leave', `user:${user.id}`);
    };
  }, [user?.id]);

  // Listen for order status changes via WebSocket
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = onOrderStatusChanged((data) => {
      const { orderId, status, totalPrice, weight, receiptNumber, adminNote, rejectedByAdmin } = data;

      if (status === 'approved') {
        // Play ding sound for order approval
        playDingSound();
        // Show approval toast
        const formattedPrice = formatPersianNumber(parseFloat(totalPrice || '0'));
        const weightNum = parseFloat(weight || '0');
        const formattedWeight = weightNum.toFixed(2);
        let message = `تراکنش به مبلغ ${formattedPrice} تومان به وزن ${formattedWeight} گرم تایید شد، برکت!`;
        if (adminNote) {
          message += `\n${adminNote}`;
        }
        toast.success(message, { autoClose: false, closeOnClick: true });
        removePendingOrder(orderId);
        bumpRejectCooldown();
      } else if (status === 'rejected') {
        // Play rejection sound for order rejection
        playRejectionSound();
        // Show rejection toast - different message for admin rejection vs auto-rejection
        let message;
        if (rejectedByAdmin) {
          message = 'درخواست شما توسط ادمین رد شد.';
          if (adminNote) {
            message += `\n${adminNote}`;
          }
        } else if (adminNote && adminNote.includes('تغییر مظنه')) {
          // Price change rejection - use the adminNote as the main message
          message = adminNote;
        } else if (adminNote && adminNote.includes('عدم تایید در مدت زمان تعیین شده')) {
          // Timeout rejection - use the adminNote as the main message
          message = adminNote;
        } else {
          message = 'تراکنش به دلیل عدم پاسخگویی ادمین توسط سیستم رد شد.';
          if (adminNote) {
            message += `\n${adminNote}`;
          }
        }
        toast.error(message, {
          autoClose: false,
          closeOnClick: true,
        });
        removePendingOrder(orderId);
        bumpRejectCooldown();
      }
    });

    return unsubscribe;
  }, [user?.id, removePendingOrder, bumpRejectCooldown]);

  // Reset minimized state when all orders are cleared
  useEffect(() => {
    if (orders.length === 0 && isMinimized) {
      setMinimized(false);
    }
  }, [orders.length, isMinimized, setMinimized]);

  // Update countdown every second
  useEffect(() => {
    if (orders.length === 0) return;

    const interval = setInterval(() => {
      const expiredOrderIds = updateCountdown();
      
      // Check expired orders and show rejection toast (fallback if socket event doesn't arrive)
      if (expiredOrderIds.length > 0) {
        expiredOrderIds.forEach((orderId) => {
          // Double-check if order is still pending by fetching from API
          fetchTraderOrdersForPendingNotifications()
            .then((response) => {
              const allOrders = getOrdersArray(response);
              const orderStatus = allOrders.find((o) => String(o.id) === String(orderId))?.status;
              if (orderStatus === 1 || orderStatus === '1' || orderStatus === 'pending_admin_approval') {
                // Fallback toast - socket event should handle this, but show message if socket fails
                toast.error('سفارش به دلیل عدم تایید در مدت زمان تعیین شده رد شد.', {
                  autoClose: false,
                  closeOnClick: true,
                });
              }
            })
            .catch((error) => {
              console.error('Error checking order status:', error);
            });
        });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [orders.length, updateCountdown]);

  const handleMinimize = () => {
    setMinimized(true);
  };

  const handleRestore = () => {
    setMinimized(false);
  };

  if (orders.length === 0) return null;

  // Show minimized tab at bottom
  if (isMinimized) {
    return (
      <button
        onClick={handleRestore}
        className="fixed bottom-16 md:bottom-4 left-1/2 transform -translate-x-1/2 z-50 bg-warning text-warning-foreground px-4 py-2 rounded-full shadow-lg flex items-center gap-2 hover:opacity-90 transition-opacity"
        aria-label="نمایش اعلان‌ها"
      >
        <Clock className="w-4 h-4" />
        <span className="font-medium">
          {orders.length} سفارش در انتظار
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 left-4 md:left-auto z-50 space-y-2 max-w-sm pointer-events-auto">
      {orders.map((order) => (
        <Card key={order.orderId} className="shadow-lg border-warning/30 bg-card relative">
          <CardContent className="p-4">
            <button
              type="button"
              onClick={handleMinimize}
              className="absolute left-2 top-2 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="کوچک کردن اعلان"
            >
              <ChevronDown className="h-4 w-4" />
            </button>
            <div className="flex items-start justify-between gap-3 pr-6">
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-warning shrink-0" />
                  <span className="font-medium">
                    تراکنش در حال بررسی توسط ادمین می‌باشد. لطفاً منتظر باشید.
                  </span>
                </div>
                {/* <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>شماره رسید: {order.systemNumber ?? "—"}</span>
                </div> */}
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-warning tabular-nums">
                    {formatCountdown(order.countdown)}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

